/**
 * finance-core — Số liệu tài chính cá nhân trung bình (KH1, quyết định 24/07/2026)
 *
 * Nguồn chân lý duy nhất cho "thu/chi/tiết kiệm trung bình tháng" — trước đây
 * forecastDataService tự tính với 3 lỗi: cộng hoàn tiền như chi tiêu, kẹp
 * tiết kiệm âm về 0, chia cứng cho 6 bất kể dữ liệu thật.
 *
 * Quy ước:
 * - Cửa sổ: tối đa `windowMonths` tháng ĐÃ KẾT THÚC gần nhất (loại tháng dở)
 * - Mẫu số: số tháng CÓ dữ liệu trong cửa sổ (floor 1) — người mới 1 tháng
 *   không bị chia 6
 * - Chi tiêu dùng CHIỀU CÓ DẤU (hoàn tiền âm) — khớp signedExpenseAmount
 * - avgMonthlySavings CÓ THỂ ÂM — thâm hụt phải hiển thị trung thực,
 *   người tiêu thụ không được kẹp về 0
 */

import { TxRecord, signedAmount, isIncome } from './aggregate';
import { LONG_TERM_TYPES } from './balance';

export interface UserFinancialMetrics {
  avgMonthlyIncome: number;
  avgMonthlyExpense: number;
  /** Có thể ÂM khi chi vượt thu — hiển thị trung thực, không kẹp về 0 */
  avgMonthlySavings: number;
  /** %/tháng; null khi chưa có thu nhập để tính tỷ lệ */
  savingsRate: number | null;
  /** Số tháng có dữ liệu thật trong cửa sổ — hiển thị "dựa trên N tháng" */
  monthsOfData: number;
}

export const userFinancialMetrics = (
  incomes: TxRecord[],
  expenses: TxRecord[],
  now: Date = new Date(),
  windowMonths = 6
): UserFinancialMetrics => {
  // Các tháng ĐÃ KẾT THÚC gần nhất (không gồm tháng hiện tại đang dở)
  const windowKeys = new Set<string>();
  for (let i = 1; i <= windowMonths; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    windowKeys.add(`${d.getFullYear()}-${d.getMonth() + 1}`);
  }
  const keyOf = (r: TxRecord) => `${r.year}-${r.month}`;

  const wIncomes = incomes.filter(r => isIncome(r) && windowKeys.has(keyOf(r)));
  const wExpenses = expenses.filter(r => windowKeys.has(keyOf(r)));

  const activeKeys = new Set<string>();
  wIncomes.forEach(r => activeKeys.add(keyOf(r)));
  wExpenses.forEach(r => activeKeys.add(keyOf(r)));
  const monthsOfData = activeKeys.size;

  const denom = Math.max(1, monthsOfData);
  const totalIncome = wIncomes.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const totalExpense = wExpenses.reduce((s, r) => s + signedAmount(r), 0);

  const avgMonthlyIncome = totalIncome / denom;
  const avgMonthlyExpense = totalExpense / denom;
  const avgMonthlySavings = avgMonthlyIncome - avgMonthlyExpense;
  const savingsRate = avgMonthlyIncome > 0
    ? (avgMonthlySavings / avgMonthlyIncome) * 100
    : null;

  return {
    avgMonthlyIncome,
    avgMonthlyExpense,
    avgMonthlySavings,
    savingsRate,
    monthsOfData
  };
};

/**
 * Tài sản THANH KHOẢN — một định nghĩa cho toàn app, trùng với balanceSheet().liquid:
 * mọi ví KHÔNG phải thẻ tín dụng và KHÔNG thuộc nhóm dài hạn (tiết kiệm, đầu tư, tài sản).
 * (Trước đây hàm này chỉ nhận cash/savings/checking nên bỏ sót ví loại "bank" và lệch với Tổng quan.)
 */
export const liquidBalance = (
  sources: Array<{ type?: string; balance?: number }>
): number =>
  sources
    .filter(s => s.type !== 'credit' && !(LONG_TERM_TYPES as readonly string[]).includes(s.type ?? ''))
    .reduce((sum, s) => sum + (Number(s.balance) || 0), 0);

export interface MonthTotals { key: string; income: number; expense: number }

/** Thu/chi từng tháng ĐÃ KẾT THÚC có dữ liệu trong cửa sổ (cùng quy ước userFinancialMetrics). */
export const monthlyTotals = (incomes: TxRecord[], expenses: TxRecord[], now: Date = new Date(), windowMonths = 6): MonthTotals[] => {
  const keys: string[] = [];
  for (let i = windowMonths; i >= 1; i--) { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); keys.push(`${d.getFullYear()}-${d.getMonth() + 1}`); }
  const rows = keys.map(key => ({ key, income: 0, expense: 0, active: false }));
  const at = new Map(rows.map(r => [r.key, r]));
  for (const r of incomes) { const t = at.get(`${r.year}-${r.month}`); if (t && isIncome(r)) { t.income += Number(r.amount) || 0; t.active = true; } }
  for (const r of expenses) { const t = at.get(`${r.year}-${r.month}`); if (t) { t.expense += signedAmount(r); t.active = true; } }
  return rows.filter(r => r.active).map(({ key, income, expense }) => ({ key, income, expense }));
};

export const median = (xs: number[]): number => {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b), mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};

/** Chi tiêu "điển hình" = trung vị theo tháng — không bị kéo lệch bởi vài tháng chi đột biến (học phí, mua sắm lớn). */
export const typicalMonthlyExpense = (totals: MonthTotals[]): number => median(totals.map(t => t.expense));
